/**
 * Named calendar presets that resolve to the shared inclusive DateRange
 * ({ from, to } as YYYY-MM-DD) used by list filters.
 * Weeks are Sunday–Saturday (JS getDay() convention).
 */

import { addCalendarDays, formatDateInputValue, parseDateInputValue } from './dateUtils'
import { EMPTY_DATE_RANGE, type DateRange } from './listFilterSort'

export type DateRangePresetId =
  | 'today'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'this_year'
  | 'custom'

export const DATE_RANGE_PRESET_IDS: DateRangePresetId[] = [
  'today',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'this_quarter',
  'this_year',
  'custom',
]

export const DATE_RANGE_PRESETS: Array<{ id: DateRangePresetId; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'this_week', label: 'This week' },
  { id: 'last_week', label: 'Last week' },
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'this_quarter', label: 'This quarter' },
  { id: 'this_year', label: 'This year' },
  { id: 'custom', label: 'Custom' },
]

export const DEFAULT_DATE_RANGE_PRESET: DateRangePresetId = 'today'

export const DASHBOARD_DATE_RANGE_STORAGE_KEY = 'dashboard.dateRange'

export interface StoredDateRangeSelection {
  preset: DateRangePresetId
  custom: DateRange
}

const DEFAULT_SELECTION: StoredDateRangeSelection = {
  preset: DEFAULT_DATE_RANGE_PRESET,
  custom: EMPTY_DATE_RANGE,
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

/** Sunday of the week containing `date`. */
export function startOfWeekSunday(date: Date): Date {
  const d = startOfDay(date)
  d.setDate(d.getDate() - d.getDay())
  return d
}

function lastDayOfMonth(year: number, monthIndex: number): Date {
  return new Date(year, monthIndex + 1, 0)
}

function isPresetId(value: unknown): value is DateRangePresetId {
  return typeof value === 'string' && (DATE_RANGE_PRESET_IDS as string[]).includes(value)
}

function normalizeCustomRange(custom: DateRange | null | undefined): DateRange {
  const from = custom?.from || ''
  const to = custom?.to || ''
  if (from && to && from > to) return { from: to, to: from }
  return { from, to }
}

export function resolveDateRangePreset(
  preset: DateRangePresetId,
  now: Date = new Date(),
  custom?: DateRange | null
): DateRange {
  const today = startOfDay(now)
  const todayYmd = formatDateInputValue(today)

  if (preset === 'custom') {
    const range = normalizeCustomRange(custom)
    if (range.from && range.to) return range
    return { from: todayYmd, to: todayYmd }
  }

  if (preset === 'today') {
    return { from: todayYmd, to: todayYmd }
  }

  if (preset === 'this_week') {
    return { from: formatDateInputValue(startOfWeekSunday(today)), to: todayYmd }
  }

  if (preset === 'last_week') {
    const thisSunday = startOfWeekSunday(today)
    const lastSunday = new Date(thisSunday)
    lastSunday.setDate(lastSunday.getDate() - 7)
    const lastSaturday = new Date(thisSunday)
    lastSaturday.setDate(lastSaturday.getDate() - 1)
    return {
      from: formatDateInputValue(lastSunday),
      to: formatDateInputValue(lastSaturday),
    }
  }

  if (preset === 'this_month') {
    return {
      from: formatDateInputValue(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: todayYmd,
    }
  }

  if (preset === 'last_month') {
    const year = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear()
    const month = today.getMonth() === 0 ? 11 : today.getMonth() - 1
    return {
      from: formatDateInputValue(new Date(year, month, 1)),
      to: formatDateInputValue(lastDayOfMonth(year, month)),
    }
  }

  if (preset === 'this_quarter') {
    const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3
    return {
      from: formatDateInputValue(new Date(today.getFullYear(), quarterStartMonth, 1)),
      to: todayYmd,
    }
  }

  return {
    from: formatDateInputValue(new Date(today.getFullYear(), 0, 1)),
    to: todayYmd,
  }
}

/**
 * Inclusive YYYY-MM-DD range → half-open timestamp bounds for timestamptz columns.
 * `gte` is the start day; `lt` is the day after `to` (exclusive).
 */
export function dateRangeTimestampBounds(range: DateRange): { gte: string | null; lt: string | null } {
  const gte = range.from || null
  const lt = range.to ? addCalendarDays(range.to, 1) : null
  return { gte, lt }
}

export function formatDateRangeLabel(
  range: DateRange,
  now: Date = new Date()
): string {
  const todayYmd = formatDateInputValue(now)
  if (!range.from && !range.to) return 'All dates'
  if (range.from && range.from === range.to) {
    if (range.from === todayYmd) return 'Today'
    const d = parseDateInputValue(range.from)
    if (!d) return range.from
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const fmt = (ymd: string) => {
    const d = parseDateInputValue(ymd)
    if (!d) return ymd
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }
  if (range.from && range.to) return `${fmt(range.from)} – ${fmt(range.to)}`
  if (range.from) return `From ${fmt(range.from)}`
  return `Through ${fmt(range.to)}`
}

export function parseStoredDateRangeSelection(raw: string | null | undefined): StoredDateRangeSelection {
  if (!raw) return { ...DEFAULT_SELECTION, custom: { ...EMPTY_DATE_RANGE } }
  try {
    const parsed = JSON.parse(raw) as Partial<StoredDateRangeSelection> & { from?: string; to?: string }
    const preset = isPresetId(parsed.preset) ? parsed.preset : DEFAULT_DATE_RANGE_PRESET
    const custom = normalizeCustomRange(
      parsed.custom || {
        from: typeof parsed.from === 'string' ? parsed.from : '',
        to: typeof parsed.to === 'string' ? parsed.to : '',
      }
    )
    return { preset, custom }
  } catch {
    return { ...DEFAULT_SELECTION, custom: { ...EMPTY_DATE_RANGE } }
  }
}

export function loadDashboardDateRangeSelection(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
): StoredDateRangeSelection {
  try {
    return parseStoredDateRangeSelection(storage?.getItem(DASHBOARD_DATE_RANGE_STORAGE_KEY) ?? null)
  } catch {
    return { ...DEFAULT_SELECTION, custom: { ...EMPTY_DATE_RANGE } }
  }
}

export function saveDashboardDateRangeSelection(
  selection: StoredDateRangeSelection,
  storage: Pick<Storage, 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
): void {
  try {
    storage?.setItem(
      DASHBOARD_DATE_RANGE_STORAGE_KEY,
      JSON.stringify({
        preset: selection.preset,
        custom: normalizeCustomRange(selection.custom),
      })
    )
  } catch {
    // private mode / quota — ignore
  }
}
